import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import { LockOutlined, Upload, YouTube } from '@mui/icons-material';
import ChannelYouTubeService from '../../services/ChannelYouTubeService';
import { gradients } from '../../theme';

function formatCount(value) {
  return value == null ? 'Unavailable' : Number(value).toLocaleString();
}

export default function UploadStoryDialog({ story, onClose, onUploaded }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const submitting = useRef(false);

  useEffect(() => {
    let active = true;
    ChannelYouTubeService.uploadPreview(
      'ai-stories',
      story.channel.id,
      story.id,
    )
      .then((data) => {
        if (active) setPreview(data);
      })
      .catch((err) => {
        if (active)
          setError(
            err.response?.data?.error ||
              'Could not load the YouTube channel. Close this dialog and try again.',
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [story.id, story.channel.id]);

  async function confirmUpload() {
    if (!preview || preview.uploadBlockedReason || submitting.current) return;
    submitting.current = true;
    setUploading(true);
    setError('');
    try {
      const upload = await ChannelYouTubeService.upload(
        'ai-stories',
        story.channel.id,
        story.id,
        preview,
      );
      onUploaded(upload);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          'Could not queue the YouTube upload. Please try again.',
      );
    } finally {
      submitting.current = false;
      setUploading(false);
    }
  }

  const channel = preview?.channel;
  return (
    <Dialog
      open
      onClose={() => !uploading && onClose()}
      maxWidth='sm'
      fullWidth
      aria-labelledby='youtube-upload-title'
      slotProps={{ paper: { sx: { bgcolor: '#fff' } } }}
    >
      <DialogTitle id='youtube-upload-title'>Upload to YouTube</DialogTitle>
      <DialogContent>
        {loading && (
          <Stack
            direction='row'
            alignItems='center'
            spacing={2}
            sx={{ py: 3 }}
            role='status'
          >
            <CircularProgress size={24} />
            <Typography>Loading connected channel...</Typography>
          </Stack>
        )}
        {preview && (
          <Stack spacing={3} sx={{ pt: 1 }}>
            {preview.uploadBlockedReason && (
              <Alert severity='warning'>{preview.uploadBlockedReason}</Alert>
            )}
            <Box
              sx={{
                background: gradients.primaryVerySubtle,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                p: 2.5,
              }}
            >
              <Typography variant='overline' color='text.secondary'>
                Destination channel
              </Typography>
              <Stack
                direction='row'
                spacing={2}
                alignItems='center'
                sx={{ mt: 1 }}
              >
                <Avatar
                  src={channel.thumbnailUrl}
                  alt={channel.name}
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                  }}
                >
                  <YouTube />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Link
                    href={`https://www.youtube.com/channel/${channel.id}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    underline='hover'
                    color='text.primary'
                    variant='h6'
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    {channel.name}
                  </Link>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    {channel.handle || channel.id}
                  </Typography>
                </Box>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  gap: 2,
                  mt: 2.5,
                }}
              >
                {[
                  [
                    'Subscribers',
                    channel.hiddenSubscriberCount
                      ? 'Hidden'
                      : formatCount(channel.subscriberCount),
                  ],
                  ['Public videos', formatCount(channel.videoCount)],
                  ['Channel views', formatCount(channel.viewCount)],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography
                      variant='h6'
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {value}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                Video title
              </Typography>
              <Typography
                sx={{
                  fontWeight: 600,
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                }}
              >
                {preview.title}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                This exact title will be used on YouTube.
              </Typography>
            </Box>
            <Stack direction='row' spacing={1.5} alignItems='center'>
              <Chip
                icon={<LockOutlined />}
                label='Private'
                size='small'
                variant='outlined'
              />
              <Typography variant='body2' color='text.secondary'>
                Only you can watch until you change visibility in YouTube
                Studio.
              </Typography>
            </Stack>
          </Stack>
        )}
        {error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Tooltip title={preview?.uploadBlockedReason || ''}>
          <span>
            <Button
              variant='contained'
              onClick={confirmUpload}
              disabled={loading || !preview || uploading || Boolean(preview.uploadBlockedReason)}
              startIcon={
                uploading ? (
                  <CircularProgress size={18} color='inherit' />
                ) : (
                  <Upload />
                )
              }
            >
              {uploading ? 'Queuing upload...' : 'Confirm upload'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
